import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '../../app/hooks';
import { ActionMenuConst } from '../../modules/Investor/Deals/components/DealsList/DealsTable/components/DealsRow/DealsRow';
import { DEAL_SOURCE_TYPES } from '../../store/investor/constants';
import { useOutsideClick } from '../../utils/constants/hooks';
import { isLenderType } from '../../utils/utils';

import threeDotVertical from '../../assets/three-dot-vertical.svg';

import styles from './ActionMenu.module.scss';

type Props = {
    triggerPayload: any;
    rowData: any;
    style?: React.CSSProperties;
    actionMenu: ActionMenuConst;
    source: string;
    disabled?: boolean;
    onThreeDotsClick?: () => void;
    excludedActions?: string[];
    includedActions?: string[];
    usePortal?: boolean;
};

const ActionMenu: React.FC<Props> = ({
    triggerPayload,
    style,
    actionMenu,
    rowData,
    source,
    disabled,
    onThreeDotsClick,
    excludedActions,
    includedActions,
    usePortal = false,
}) => {
    const location = useLocation();
    const actionMenuItems = rowData?.mainConfig?.actionConfig || [];
    const userData = useAppSelector((state) => state.user.userData);
    const [menuItems, setMenuItems] = useState<string[]>([]);
    const [showMenu, setShowMenu] = useState<boolean>(false);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isCompanyDealsPage = location.pathname.includes('company-deals');

    const threeDotRef = useRef<HTMLImageElement | null>(null);

    useEffect(() => {
        let updatedItems = [...actionMenuItems];

        if (rowData?.mandateState === 'DRAFT' || source === DEAL_SOURCE_TYPES.DEAL) {
            const index = updatedItems.indexOf('Edit Deal');
            if (index !== -1) {
                updatedItems[index] = 'Edit Mandate';
            }
        }

        includedActions?.forEach((action) => {
            if (!updatedItems.includes(action)) {
                updatedItems.push(action);
            }
        });

        if (source === DEAL_SOURCE_TYPES.DEAL || source === DEAL_SOURCE_TYPES.INVESTOR_DEAL) {
            if (updatedItems.length > 0) {
                if (!updatedItems.includes('One Pager')) {
                    updatedItems.push('One Pager');
                }
            }

            if (isCompanyDealsPage) {
                updatedItems = updatedItems.filter((item) => item !== 'AICA Report');
            }

            if (isLenderType(userData.accountType)) {
                updatedItems = updatedItems.filter((item) => item !== 'Add Lender');
            }

            if (excludedActions) {
                updatedItems = updatedItems.filter((item) => !excludedActions.includes(item));
            }

            setMenuItems(updatedItems);
        } else if (updatedItems.length > 0) {
            setMenuItems(updatedItems);
        }
    }, [actionMenuItems, source, rowData, includedActions]);

    useOutsideClick(threeDotRef, () => {
        closeMenu();
    });

    const calculatePortalPosition = () => {
        if (threeDotRef.current && usePortal) {
            const rect = threeDotRef.current.getBoundingClientRect();

            let menuWidth = 180;
            if (dropdownRef.current) {
                menuWidth = dropdownRef.current.offsetWidth;
            }

            const top = rect.bottom + window.scrollY + 5;
            let left = rect.right + window.scrollX - menuWidth;

            const viewportWidth = window.innerWidth;
            const scrollX = window.scrollX;
            const padding = 10;

            if (left + menuWidth > viewportWidth + scrollX - padding) {
                left = viewportWidth + scrollX - menuWidth - padding;
            }

            if (left < scrollX + padding) {
                left = scrollX + padding;
            }

            setMenuPosition({
                top,
                left,
            });
        }
    };

    useEffect(() => {
        if (showMenu && usePortal && dropdownRef.current) {
            setTimeout(() => {
                calculatePortalPosition();
            }, 0);
        }
    }, [showMenu, usePortal]);

    const toggleMenu = () => {
        if (disabled) return;
        onThreeDotsClick?.();

        if (usePortal && !showMenu) {
            calculatePortalPosition();
        }

        setShowMenu(!showMenu);
    };

    const closeMenu = () => {
        setShowMenu(false);
    };

    const handleMenuItemClick = (item: string) => {
        actionMenu?.[item]?.action?.(triggerPayload, source);
        closeMenu();
    };

    const dropdownContent = (
        <div
            ref={dropdownRef}
            style={{
                ...(usePortal
                    ? {
                          position: 'fixed',
                          top: style?.top ?? menuPosition.top,
                          left: style?.left ?? menuPosition.left,
                          zIndex: 999999,
                      }
                    : {}),
                ...style,
            }}
            className={clsx(styles.Options)}
            onClick={(e) => e.stopPropagation()}
        >
            {menuItems?.map((item, index) => (
                <div
                    key={index}
                    className={clsx({
                        [styles.Option]: true,
                        [styles.Hide]: !actionMenuItems.includes('One Pager') && item === 'One Pager',
                        [styles.Destructive]: item.toLowerCase().includes('delete'),
                    })}
                    onMouseDown={() => handleMenuItemClick(item)}
                >
                    {item}
                </div>
            ))}
        </div>
    );

    return (
        <div className={styles.Action}>
            {actionMenuItems?.length != null && (
                <div
                    className={clsx(styles.ThreeDotBox, {
                        [styles.ThreeDotActive]: !!showMenu,
                        [styles.Disabled]: disabled,
                    })}
                >
                    <img
                        src={threeDotVertical}
                        alt="upl"
                        height="30px"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleMenu();
                        }}
                        ref={threeDotRef}
                    />
                </div>
            )}

            {showMenu && (usePortal ? createPortal(dropdownContent, document.body) : dropdownContent)}
        </div>
    );
};

export default ActionMenu;
